import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Eye, Search, FilterX } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";

import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { GenericTable } from "@shared/components/GenericTable";
import { PAGE_SIZE } from "@shared/utils/constants";
import { TableAction, TableColumn } from "@shared/types/table";
import {
  useChoices,
  useDeviceManagementMeters,
  useDeviceManagementMetersExport,
} from "@features/mx/device-management/hooks";

export const AllMeters: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL Params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentStatus = searchParams.get("status") || "all";
  const currentMeterType = searchParams.get("meterType") || "ALL";

  // UI State for pending edits
  const [pendingFilters, setPendingFilters] = useState({
    status: decodeURIComponent(currentStatus),
    meterType: decodeURIComponent(currentMeterType),
  });

  // Sync pendingFilters when URL changes
  useEffect(() => {
    setPendingFilters({
      status: decodeURIComponent(currentStatus),
      meterType: decodeURIComponent(currentMeterType),
    });
  }, [currentStatus, currentMeterType]);

  const queryParams = useMemo(
    () => ({
      remoteUtilityId,
      page: currentPage,
      limit: PAGE_SIZE,
      searchData: currentSearch,
      status: currentStatus,
      meterType: currentMeterType,
    }),
    [
      remoteUtilityId,
      currentPage,
      currentSearch,
      currentStatus,
      currentMeterType,
    ],
  );

  const { data: metersList, refetch } = useDeviceManagementMeters(queryParams);
  const { data: meterStatuses } = useChoices({ choice: "meter_status" });
  const { data: meterTypes } = useChoices({ choice: "meter_type" });

  const { exportCSV, isExporting } = useDeviceManagementMetersExport(
    remoteUtilityId,
    currentSearch,
    currentStatus,
    currentMeterType,
  );

  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);

  const handlePendingFilterChange = (
    key: keyof typeof pendingFilters,
    value: string,
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    console.log("pendingFilters", pendingFilters);
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.status && { status: pendingFilters.status }),
      ...(pendingFilters.meterType && { meterType: pendingFilters.meterType }),
      page: 1,
    };

    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.status;
    delete newParams.meterType;
    delete newParams.search;
    newParams.page = 1;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(currentStatus && { status: currentStatus }),
      ...(currentMeterType && { meterType: currentMeterType }),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
  };

  const handleExport = () => {
    exportCSV();
  };

  const columns: TableColumn<any>[] = [
    { key: "meterNumber", header: "Meter No", sortable: true },
    { key: "deviceNo", header: "Device No", sortable: true },
    { key: "productCode", header: "Utility Service", sortable: true },
    { key: "premiseName", header: "Premise", sortable: true },
    {
      key: "firstName",
      header: "Consumer Name",
      sortable: true,
      type: "custom",
      render: (_value, row) => {
        const fullName = [row?.firstName, row?.lastName].filter(Boolean).join(" ");
        return <span>{fullName || "N/A"}</span>;
      },
    },
    { key: "consumerNo", header: "Account Number", sortable: true },
    { key: "installedOn", header: "Install Date", sortable: true },
    { key: "status", header: "Status" },
    { key: "meterReading", header: "Last Reading" },
  ];

  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: (row: any) => {
        navigate(`/mx/device-management/meter/view/${row.id}`);
      },
    },
  ];

  const pagination = metersList
    ? {
        currentPage,
        totalPages: Math.ceil(metersList.count / PAGE_SIZE),
        totalItems: metersList.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, metersList.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const FilterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <Select
            value={pendingFilters.status}
            onValueChange={(value) =>
              handlePendingFilterChange("status", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {meterStatuses?.map((status) => (
                <SelectItem key={status.key} value={String(status.value)}>
                  {status.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Type</label>
          <Select
            value={pendingFilters.meterType}
            onValueChange={(value) =>
              handlePendingFilterChange("meterType", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              {meterTypes?.map((type) => (
                <SelectItem key={type.key} value={String(type.key)}>
                  {type.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters =
    currentStatus !== "all" || currentMeterType !== "ALL";

  return (
    <GenericTable
      data={metersList?.results}
      columns={columns}
      rowKey="id"
      actions={actions}
      actionsType="icons"
      pagination={pagination}
      onPageChange={handlePageChange}
      onSearch={handleSearch}
      searchPlaceholder="Search meters..."
      searchDebounceMs={500}
      filterPanel={FilterPanel}
      onExport={handleExport}
      exportLoading={isExporting}
      emptyMessage="No meters found"
      emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
      filters={
        hasActiveFilters ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Filters active
            </span>
          </div>
        ) : null
      }
    />
  );
};
